import React, { createContext, FC } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ConfirmModal, FormInput } from '@microservices/wiskey-react-components'
import { Box, Button, Grid, Typography } from '@mui/material'

import { VariablesTab } from '@pages/ContextMenuCreateOrEdit/components/VariablesTab'
import { BtnCopyConfigExample } from '@pages/GanttChart/components/ConfigExamples'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { DataSource } from '@components/DataSource'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { NewModel } from '@components/NewModel'
import { ScriptValueDialog } from '@components/ScriptValueDialog'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'
import { Variables } from '@components/Variables'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import {
  ENTITY_CONFIG_TYPE,
  ENTITY_TYPES_REQUEST,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  MODEL_TYPE,
  REGEX,
  TIMELINE_ACTION_COLUMNS,
  TOOLTIP_POINT_COLUMNS,
} from '@constants'

import { FieldsConfig } from './components/ConfigFields/FieldsConfig'
import { useTooltipCreateOrEdit } from './hooks'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const TooltipCreateOrEdit: FC = () => {
  const { t } = useTranslation()

  const {
    state: {
      currentTab,
      currentRowId,
      disabledTab,
      isOpenDialog,
      isShowScriptValueDialogJson,
      isShowDeleteVariableModal,
      showAddActionDialog,
      addActionDialogData,
    },
    data: {
      modelType,
      methods,
      copyId,
      id,
      isEdit,
      isDirty,
      objects,
      isLoadingObjects,
      isLoadingTooltipById,
      objectFields,
      variableFields,
      watchedIsJson,
      watchedJsonValue,
      watchedPoints,
      watchedVariables,
      isLoadingTooltip,
      paramsId,
      watchedActions,
      isUpdateActionProcessing,
    },
    handlers: {
      setShowScriptValueDialogJson,
      handleSave,
      handleCancel,
      handleTabChange,
      handleChangeJsonMode,
      handleChangeValueScriptJson,
      handleOpenPointDialog,
      handleClosePointDialog,
      handleSavePoint,
      handleEditPoint,
      handleDeletePoint,
      handleAddVariableItem,
      handleSaveVariable,
      handleDeleteVariable,
      handleOpenDeleteVariableModal,
      handleCloseDeleteVariableModal,
      handleChangeAction,
      handleOpenAddActionDialog,
      handleDeleteAction,
      handleCloseAddActionDialog,
    },
  } = useTooltipCreateOrEdit()

  const contextValue: PageContextType = {
    isOpenDialog,
    isLoadingTooltip,
    objectFields,
    points: watchedPoints,
    variables: watchedVariables,
    currentRowId,
    handleSavePoint,
    handleClosePointDialog,
    handleChangeAction,
    handleCloseAddActionDialog,
    addActionDialogData,
    id,
    isUpdateActionProcessing,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.TOOLTIPS}>
        <Box>
          <EditConfigScriptDialog />
          <FormProvider {...methods}>
            <Box px={1} py={0}>
              <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
                <Typography variant='h5'>{t(`tooltips.title.${modelType}`)}</Typography>
                {isEdit && <EditConfigMenu configId={id} />}
              </Grid>
              <NewModel loading={isLoadingTooltipById} />
              <FormInput
                formInputContainerSx={{ mt: 0.5 }}
                inputType={GENERATOR_INPUT_TYPE.INPUT}
                label={t('tooltips.allTooltipsTable.width.label')}
                loading={isLoadingTooltipById}
                maxLengthInput={4}
                name='width'
                placeholder={t('tooltips.allTooltipsTable.width.placeholder')}
                replacePattern={REGEX.REPLACE_ONLY_NUMBER}
              />
              <DataSource
                isDisabledObject={Boolean(id || copyId)}
                isLoading={isLoadingTooltipById || isLoadingObjects}
                objects={objects}
              />
            </Box>
            <Grid container justifyContent='flex-end'>
              <GeneralButtons
                cancelText={t('viewOrFormCreate.cancelBtnText')}
                isLoading={isLoadingTooltip || isLoadingTooltipById}
                saveDisabled={!isDirty || isLoadingTooltip || isLoadingTooltipById}
                saveText={t('viewOrFormCreate.saveBtnText')}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            </Grid>
            <PageContentLayout key={currentTab} sx={{ pt: 1, px: 1 }}>
              {isShowScriptValueDialogJson && (
                <ScriptValueDialog
                  windowBoundaries
                  isShow={isShowScriptValueDialogJson}
                  language='json'
                  title={t('tooltips.title.editJson')}
                  value={watchedJsonValue || ''}
                  onClose={() => setShowScriptValueDialogJson(false)}
                  onSave={handleChangeValueScriptJson}
                />
              )}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Tabs value={currentTab} onChange={handleTabChange}>
                  <Tab disabled={disabledTab} label={t('tooltips.tabs.tooltipPoints')} />
                  <Tab disabled={disabledTab} label={t('tooltips.tabs.config')} />
                  <Tab disabled={disabledTab} label={t('tooltips.tabs.actions')} />
                  <Tab disabled={disabledTab} label={t('tooltips.tabs.variables')} />
                  <Tab disabled={disabledTab} label={t('tooltips.tabs.staticVariables')} />
                </Tabs>
                <Box display='flex'>
                  <Box alignItems='center' display='flex' flexWrap='wrap'>
                    <BtnCopyConfigExample name={t('tooltips.title.example')} />
                  </Box>
                  <FormInput
                    disabled={!id}
                    formInputContainerSx={{ mb: 0 }}
                    formLabelSx={{ minWidth: 90, width: 95 }}
                    inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                    label={t('label.json')}
                    labelPlacement='end'
                    labelSx={{ minWidth: 60, width: 60 }}
                    name='isJson'
                    onChangeCheckbox={handleChangeJsonMode}
                  />
                </Box>
              </Box>
              {watchedIsJson ? (
                <Box>
                  <FormInput
                    formLabelSx={{ my: 2 }}
                    inputType={GENERATOR_INPUT_TYPE.TEXTAREA}
                    maxLengthInput={MAX_INPUT_LENGTH}
                    name='jsonValue'
                    placeholder={t('contextMenuCreate.addMenuPointModal.titleJsValue.placeholder')}
                    readOnly={true}
                    rules={{ maxLength: MAX_INPUT_LENGTH }}
                    additionalBtn={{
                      isEnabled: true,
                      text: 'edit',
                      color: 'primary',
                      variant: 'contained',
                      onClick: () => setShowScriptValueDialogJson(true),
                    }}
                  />
                </Box>
              ) : (
                <Box>
                  <TabPanel index={0} value={currentTab}>
                    <SwitchTable
                      disablePagination
                      isCrud
                      btnDisabled={!isEdit || isLoadingTooltip}
                      btnText={t(`tooltips.button.addTooltipPoint`)}
                      columns={TOOLTIP_POINT_COLUMNS}
                      loading={isLoadingTooltipById || isLoadingTooltip}
                      rows={watchedPoints || []}
                      showActionsColumn={true}
                      showDialog={isOpenDialog}
                      type={MODEL_TYPE.TOOLTIP}
                      containerSx={{
                        '.MuiTableBody-root > div': {
                          height: '400px !important',
                        },
                      }}
                      onDelete={handleDeletePoint}
                      onEdit={handleEditPoint}
                      onShowDialog={handleOpenPointDialog}
                    />
                  </TabPanel>
                  <TabPanel index={1} value={currentTab}>
                    <FieldsConfig tooltipId={id} />
                  </TabPanel>
                  <TabPanel index={2} value={currentTab}>
                    <SwitchTable
                      disablePagination
                      disableSort
                      showActionsColumn
                      btnDisabled={!paramsId}
                      btnText={t(`timeLinesCreateOrEdit.addAction`)}
                      columns={TIMELINE_ACTION_COLUMNS}
                      rows={watchedActions}
                      showDialog={showAddActionDialog}
                      type={MODEL_TYPE.TOOLTIP_ACTION}
                      onDelete={handleDeleteAction}
                      onShowDialog={handleOpenAddActionDialog}
                      onEdit={params => {
                        handleOpenAddActionDialog('edit', params.row.original.id)
                      }}
                    />
                  </TabPanel>
                  <TabPanel index={3} value={currentTab}>
                    <Grid display='flex' justifyContent='flex-end' mb={1}>
                      <Button variant='contained' onClick={() => handleAddVariableItem()}>
                        {t('tooltips.button.addTooltipVariable')}
                      </Button>
                    </Grid>
                    <VariablesTab
                      variables={variableFields}
                      onDeleteVariable={handleOpenDeleteVariableModal}
                      onSaveVariable={handleSaveVariable}
                    />
                    <ConfirmModal
                      actionBtnText={t('modal.delete.btn')}
                      isShow={isShowDeleteVariableModal}
                      text={t('modal.delete.warning')}
                      title={t('modal.delete.title')}
                      containerStyle={{
                        px: 0.5,
                        py: 0.5,
                        // borderRadius: 0
                      }}
                      onClose={handleCloseDeleteVariableModal}
                      onConfirm={handleDeleteVariable}
                    />
                  </TabPanel>
                  <TabPanel index={4} value={currentTab}>
                    <Variables
                      entityId={paramsId}
                      entityType={ENTITY_TYPES_REQUEST.TOOLTIP}
                      isEditEntity={Boolean(paramsId)}
                    />
                  </TabPanel>
                </Box>
              )}
            </PageContentLayout>
          </FormProvider>
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
